import { LEGACY_IDENTITY_ID_PATTERN } from '../identity/legacy-identity-id';

import type { ExperimentAssignment, ExperimentMetric, ExperimentTarget } from './schema';

export type ExperimentAnalyticsProperties = Record<string, string | number | boolean>;

export interface ExperimentAnalyticsEvent {
  event: 'experiment_assigned' | 'experiment_exposed' | 'experiment_outcome';
  properties: ExperimentAnalyticsProperties;
}

const UNSAFE_VALUE =
  /(@|\b\d{3}[-. ]?\d{3}[-. ]?\d{4}\b|\b(?:condition|substance|alcohol|opioid|tobacco|cannabis|gambling|vaping|smoking|drinking|transcript|answer|prompt)\b)/i;
// The member-id shape, not RFC 4122 v4. A version-constrained pattern lets an
// imported backend2 id through, so a migrated member's id would land in an
// experiment property where a minted member's id is refused.
const MEMBER_ID_VALUE = LEGACY_IDENTITY_ID_PATTERN;
const UNSAFE_NORMALIZED_KEYS = new Set([
  'accountid',
  'address',
  'answer',
  'answertext',
  'body',
  'contact',
  'dateofbirth',
  'details',
  'dob',
  'email',
  'freetext',
  'id',
  'identity',
  'identityid',
  'member',
  'memberid',
  'message',
  'name',
  'notes',
  'patient',
  'patientid',
  'phone',
  'prompt',
  'provideragent',
  'provideragentid',
  'providerid',
  'rawprovideragentid',
  'text',
  'transcript',
  'user',
  'userid',
]);

export function buildExperimentAssignedEvent(
  assignment: ExperimentAssignment,
): ExperimentAnalyticsEvent {
  return {
    event: 'experiment_assigned',
    properties: baseProperties(assignment),
  };
}

export function buildExperimentExposedEvent(
  assignment: ExperimentAssignment,
  target: ExperimentTarget,
  extra: ExperimentAnalyticsProperties = {},
): ExperimentAnalyticsEvent {
  return {
    event: 'experiment_exposed',
    properties: safeProperties({
      ...baseProperties(assignment),
      ...featureProperties(assignment),
      ...targetProperties(target),
      ...extra,
    }),
  };
}

export function buildExperimentOutcomeEvent(
  assignment: ExperimentAssignment | null,
  metric: ExperimentMetric,
  extra: ExperimentAnalyticsProperties = {},
): ExperimentAnalyticsEvent {
  if (!assignment) {
    return {
      event: 'experiment_outcome',
      properties: safeProperties({
        metric_key: metric.id,
        outcome_event: metric.event,
        ...extra,
      }),
    };
  }

  return {
    event: 'experiment_outcome',
    properties: safeProperties({
      ...baseProperties(assignment),
      ...featureProperties(assignment),
      metric_key: metric.id,
      outcome_event: metric.event,
      ...extra,
    }),
  };
}

export function safeProperties(properties: Record<string, unknown>): ExperimentAnalyticsProperties {
  const safe: ExperimentAnalyticsProperties = {};
  for (const [key, value] of Object.entries(properties)) {
    if (!isSafeScalar(value)) {
      throw new Error(`Unsafe experiment analytics property "${key}"`);
    }
    if (
      isUnsafeKey(key) ||
      UNSAFE_VALUE.test(key) ||
      (typeof value === 'string' && (UNSAFE_VALUE.test(value) || MEMBER_ID_VALUE.test(value)))
    ) {
      throw new Error(`Unsafe experiment analytics property "${key}"`);
    }
    safe[key] = value;
  }
  return safe;
}

function isUnsafeKey(key: string): boolean {
  const normalized = key.replace(/[^a-z0-9]/gi, '').toLowerCase();
  return UNSAFE_NORMALIZED_KEYS.has(normalized);
}

function isSafeScalar(value: unknown): value is string | number | boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

function baseProperties(assignment: ExperimentAssignment): ExperimentAnalyticsProperties {
  return safeProperties({
    experiment_key: assignment.experiment_key,
    experiment_version: assignment.experiment_version,
    assignment_source: assignment.assignment_source,
    flag_key: assignment.flag_key,
    variant_key: assignment.variant_key,
    assignment_id: assignment.assignment_id,
    bucket: assignment.bucket,
    target_scopes: assignment.targets.map((target) => target.scope).join(','),
    ...(assignment.client_platform ? { client_platform: assignment.client_platform } : {}),
    ...(assignment.locale ? { locale: assignment.locale } : {}),
  });
}

function featureProperties(assignment: ExperimentAssignment): ExperimentAnalyticsProperties {
  return {
    [`$feature/${assignment.flag_key}`]: assignment.variant_key,
  };
}

function targetProperties(target: ExperimentTarget): ExperimentAnalyticsProperties {
  switch (target.scope) {
    case 'agent_definition':
      return {
        target_scope: target.scope,
        target_id: target.agent_definition_id,
        target_version: target.agent_definition_version,
        agent_definition_id: target.agent_definition_id,
        agent_definition_version: target.agent_definition_version,
      };
    case 'agent_screen_element':
      return {
        target_scope: target.scope,
        target_id: target.agent_screen_element_id,
        target_version: target.agent_screen_version,
        agent_definition_id: target.agent_definition_id,
        agent_definition_version: target.agent_definition_version,
        agent_screen_id: target.agent_screen_id,
        agent_screen_version: target.agent_screen_version,
        agent_screen_element_id: target.agent_screen_element_id,
      };
    case 'first_message':
      return {
        target_scope: target.scope,
        target_id: target.first_message_pool,
        target_version: target.agent_definition_version,
        agent_definition_id: target.agent_definition_id,
        agent_definition_version: target.agent_definition_version,
        first_message_pool: target.first_message_pool,
      };
    case 'genui_flow':
      return {
        target_scope: target.scope,
        target_id: target.flow_id,
        target_version: target.flow_version,
        flow_id: target.flow_id,
        flow_version: target.flow_version,
      };
    case 'genui_step':
      return {
        target_scope: target.scope,
        target_id: target.step_key,
        target_version: target.flow_version,
        flow_id: target.flow_id,
        flow_version: target.flow_version,
        step_key: target.step_key,
        screen_id: target.screen_id,
      };
    case 'genui_element':
      return {
        target_scope: target.scope,
        target_id: target.element_key,
        target_version: target.flow_version,
        flow_id: target.flow_id,
        flow_version: target.flow_version,
        step_key: target.step_key,
        screen_id: target.screen_id,
        element_key: target.element_key,
      };
    case 'genui_surface':
      return {
        target_scope: target.scope,
        target_id: target.surface_id,
        target_version: target.schema_version,
        surface_id: target.surface_id,
        schema_version: target.schema_version,
        screen_id: target.screen_id,
      };
    case 'genui_surface_element':
      return {
        target_scope: target.scope,
        target_id: target.element_key,
        target_version: target.schema_version,
        surface_id: target.surface_id,
        schema_version: target.schema_version,
        screen_id: target.screen_id,
        element_key: target.element_key,
      };
  }
}
